import { useState } from 'react'
import { styled } from '@linaria/react'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { commandPayload, commands } from '../../lib/runtime'

/**
 * One-tap hygiene report on a completed order (verification spec 3.2).
 *
 * A report pauses the kitchen at once, before any investigation, so the
 * wording stays factual and the confirmation says what happens next. The
 * customer is told their report was received; they are never told the
 * kitchen's resulting status, which is not theirs to see.
 */

const REASONS = [
  { value: 'undercooked', label: 'Undercooked' },
  { value: 'packaging', label: 'Poor or unsafe packaging' },
  { value: 'illness', label: 'It made me unwell' },
  { value: 'other', label: 'Something else' },
] as const

const Style = {
  Root: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
    border-top: 1px solid ${minimalVars.color.borderSubtle};
    padding-top: ${minimalVars.space.xs};
  `,
  Link: styled.button`
    justify-self: start;
    border: 0;
    background: transparent;
    padding: 0;
    color: ${minimalVars.color.textSecondary};
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    text-decoration: underline;
    cursor: pointer;
  `,
  Choices: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  `,
  Choice: styled.button<{ $on: boolean }>`
    border-radius: 999px;
    padding: 6px 12px;
    border: 1px solid ${({ $on }) => ($on ? minimalVars.color.brand : minimalVars.color.borderSubtle)};
    background: ${({ $on }) => ($on ? minimalVars.color.brandSoft : minimalVars.color.bgSurface)};
    color: ${minimalVars.color.textPrimary};
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    cursor: pointer;
  `,
  Detail: styled.textarea`
    min-height: 64px;
    padding: 8px 10px;
    border-radius: ${minimalVars.radius.md};
    border: 1px solid ${minimalVars.color.borderSubtle};
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    resize: vertical;
  `,
}

export const HygieneReport = ({ orderId }: { orderId: string }) => {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [detail, setDetail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      commandPayload(await commands.verification.reportHygieneIssue(orderId, reason, detail.trim()))
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not send your report. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <Style.Root>
        <MinimalAlert tone="success" title="Thank you, we have your report">
          Food safety comes first: the kitchen stops taking orders while our team reviews this. We will contact you if
          we need more detail. If you feel unwell, please see a doctor.
        </MinimalAlert>
      </Style.Root>
    )
  }

  return (
    <Style.Root>
      {!open ? (
        <Style.Link type="button" onClick={() => setOpen(true)}>
          Report a hygiene issue
        </Style.Link>
      ) : (
        <>
          <Style.Choices role="radiogroup" aria-label="What went wrong?">
            {REASONS.map((r) => (
              <Style.Choice key={r.value} type="button" role="radio" aria-checked={reason === r.value}
                $on={reason === r.value} onClick={() => setReason(r.value)}>
                {r.label}
              </Style.Choice>
            ))}
          </Style.Choices>
          <Style.Detail
            aria-label="Tell us more (optional)"
            placeholder="Tell us more (optional)"
            maxLength={1000}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
          />
          {error ? <MinimalAlert tone="danger" title="Not sent">{error}</MinimalAlert> : null}
          <MinimalButton size="sm" variant="secondary" disabled={!reason || busy} loading={busy} onClick={() => void submit()}>
            Send report
          </MinimalButton>
        </>
      )}
    </Style.Root>
  )
}
